import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useSwitchableOrganisations } from '@/hooks/useOrganisations';
import { ALL_ORGANISATIONS } from '@/hooks/useOrganisationFilter';

const STORAGE_KEY = 'selectedOrganisationId';

interface SelectedOrganisationContextValue {
	/** `ALL_ORGANISATIONS` or an organisation id as a string. Only a GlobalAdmin can change it. */
	selectedOrganisationId: string;
	setSelectedOrganisationId: (value: string) => void;
}

const SelectedOrganisationContext = createContext<SelectedOrganisationContextValue>({
	selectedOrganisationId: ALL_ORGANISATIONS,
	setSelectedOrganisationId: () => {},
});

function readStoredValue(): string {
	try {
		return localStorage.getItem(STORAGE_KEY) ?? ALL_ORGANISATIONS;
	} catch {
		return ALL_ORGANISATIONS;
	}
}

/**
 * The organisation a GlobalAdmin is viewing the app in, chosen from the user menu. Every register
 * list, its counts and the dashboard read it through `useSelectedOrgScope`. Detail pages do not:
 * they open a record by id in whatever organisation it belongs to.
 */
export function SelectedOrganisationProvider({ children }: { children: ReactNode }) {
	const { canChooseOrganisation } = usePermissions();
	const { organisations, loading } = useSwitchableOrganisations(canChooseOrganisation);
	const [storedOrganisationId, setSelected] = useState(readStoredValue);

	const setSelectedOrganisationId = useCallback((value: string) => {
		setSelected(value);
		try {
			localStorage.setItem(STORAGE_KEY, value);
		} catch {
			// Storage can be unavailable (private mode); the choice then lasts for this session only.
		}
	}, []);

	// A stored organisation that was deleted, or one from another account in this browser.
	const isUnknown =
		canChooseOrganisation &&
		!loading &&
		storedOrganisationId !== ALL_ORGANISATIONS &&
		!organisations.some((o) => String(o.id) === storedOrganisationId);
	const selectedOrganisationId = isUnknown ? ALL_ORGANISATIONS : storedOrganisationId;

	const value = useMemo(
		() => ({ selectedOrganisationId, setSelectedOrganisationId }),
		[selectedOrganisationId, setSelectedOrganisationId],
	);

	return <SelectedOrganisationContext.Provider value={value}>{children}</SelectedOrganisationContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSelectedOrganisation(): SelectedOrganisationContextValue {
	return useContext(SelectedOrganisationContext);
}
